import React from 'react';
import isEqual from 'lodash.isequal';
import { Dispatch } from 'redux';
import { AppState, Booking, BookingsFilter, filterBookingsOnOwnerId, filterBookingsOnType } from 'acscp/store';
import { BookingsRequest, RequestClass } from 'acscp/api/requests';
import { FetchRequest } from 'acscp/actions';
import bookingsComparison from 'acscp/store/helpers/sortBookings';
import { BookingsList } from 'acscp/components/bookings/bookingsList';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { CollapsableSet } from 'acscp/components/common/collapsable';
import { ExpandAllProps } from 'acscp/components/common/collapsable';
import classNames from 'classnames';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';
import { FormattedMessage } from 'react-intl';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import style from './bookingsLists.module.scss';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { getDomIdForFilter } from '../common/helpers/getDomIDs';
import { getViewportWidth, WidthPresets } from '../common/helpers/viewportHelpers';
import { PageHeaderRow } from 'acscp/components/common/pageHeaderRow';
import { FlownBookingsLink } from 'acscp/components/bookings/flownBookingsLink';

const messages = defineMessages({
    allCharters: {
        id: 'components.bookings.bookingsLists.allCharters',
        defaultMessage: 'All Charters'
    },
    myCharters: {
        id: 'components.bookings.bookingsLists.myCharters',
        defaultMessage: 'My Charters'
    }
});

type BookingsListsOwnProps = InjectedIntlProps;

interface BookingsListsDispatchProps {
    fetchBookings: () => void;
}

interface BookingsListsStateProps extends RequestStateProps {
    isLoggedIn: boolean;
    enquiryBookings: Booking[];
    quotedBookings: Booking[];
    bookedBookings: Booking[];
}

interface PageState {
    categoryFilterId: string;
}

const ExpandAll: React.SFC<ExpandAllProps> = ({ collapsed, setCollapsed = () => { } }) => (
    <div
        className={classNames(style.ExpandAll, { [style.AllExpanded]: !collapsed })}
        onClick={() => setCollapsed(!collapsed)}
    >
        <ArrowIcon />
        {collapsed ?
            <FormattedMessage id={'components.bookings.bookingsLists.expandAll'} defaultMessage={'Expand All'} />
            : <FormattedMessage id={'components.bookings.bookingsLists.collapseAll'} defaultMessage={'Collapse All'} />}
    </div>
);

export class BookingsListsPresentational
    extends PresentationComponentBase<BookingsListsOwnProps,
    BookingsListsStateProps,
    BookingsListsDispatchProps,
    PageState> {

    constructor(props: {} & BookingsListsStateProps & BookingsListsOwnProps & BookingsListsDispatchProps) {
        super(props);
        this.state = {
            categoryFilterId: '' // default to no filtering
        };
    }

    componentDidMount(): void {
        this.props.fetchBookings();
    }

    // React is only performing a shallow comparison of the props.
    // This is causing React to think that arrays in the props are new and so re-renders everything,
    // whereas they often have the same contents. We perform a deep comparison to check.
    shouldComponentUpdate(nextProps: BookingsListsStateProps & BookingsListsDispatchProps, nextState: PageState): boolean {
        return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
    }

    getBookingOwnerIdFilterOptions(allBookings: Booking[]): JSX.Element[] {
        let categoryOptions: JSX.Element[] = [];

        const firstBookingOwnedByCurrentUser = allBookings.find(value => value.ownedByCurrentUser);
        if (firstBookingOwnedByCurrentUser !== undefined) {
            categoryOptions = categoryOptions.concat((
                <option key={firstBookingOwnedByCurrentUser.ownerOfBookingId} value={firstBookingOwnedByCurrentUser.ownerOfBookingId}>
                    {this.props.intl.formatMessage(messages.myCharters)}
                </option>
            ));
        }

        categoryOptions = categoryOptions.concat(allBookings
            .filter((value, index, self) => {
                return !value.ownedByCurrentUser &&
                    self.map(b => b.ownerOfBookingId).indexOf(value.ownerOfBookingId) === index;
            })

            .sort((a, b) => a.ownerOfBookingName.localeCompare(b.ownerOfBookingName))
            .map(booking => (<option key={booking.ownerOfBookingId} value={booking.ownerOfBookingId}>{booking.ownerOfBookingName}</option>)
            ));

        if (categoryOptions.length > 1) {
            categoryOptions = [(
                <option key={''} value={''}>
                    {this.props.intl.formatMessage(messages.allCharters)}
                </option>
            ), ...categoryOptions];
        }

        return categoryOptions;
    }

    render(): JSX.Element {
        if (this.props.isLoading) {
            return (
                <div className={style.Loading}>
                    <LoadingIndicator />
                </div>
            );
        }
        const allBookings = this.props.enquiryBookings
            .concat(this.props.quotedBookings)
            .concat(this.props.bookedBookings);
        const categoryOptions = this.getBookingOwnerIdFilterOptions(allBookings);

        const initiallyExpanded = getViewportWidth() >= WidthPresets.tabletMin;

        const enquiryBookingsToRender = filterBookingsOnOwnerId(this.props.enquiryBookings, this.state.categoryFilterId)
            .filter(value => this.props.isLoggedIn || !value.ownedByCurrentUser);
        const quotedBookingsToRender = filterBookingsOnOwnerId(this.props.quotedBookings, this.state.categoryFilterId)
            .filter(value => this.props.isLoggedIn || !value.ownedByCurrentUser);
        const bookedBookingsToRender = filterBookingsOnOwnerId(this.props.bookedBookings, this.state.categoryFilterId)
            .filter(value => this.props.isLoggedIn || !value.ownedByCurrentUser);
        return (
            <div>
                <PageHeaderRow
                    leftContent={(
                        <p className={style.HeaderRowTitle}>
                            <FormattedMessage
                                id={'components.bookings.bookingsLists.listOfUpcomingEnquiries'}
                                defaultMessage={'UPCOMING CHARTERS'}
                            />
                        </p>
                    )}
                    showLoadingIndicator={false}
                    rightContent={categoryOptions.length > 1 ? (
                        <p className={style.FilterContainer}>
                            <FormattedMessage
                                id={'components.bookings.bookingsLists.filterChartersLabel'}
                                defaultMessage={'Filter'}
                            />
                            <select
                                onChange={v => this.setState({categoryFilterId: v.target.value})}
                                className={style.Filter}
                            >
                                {categoryOptions}
                            </select>
                        </p>
                    ) : null}
                />

                <CollapsableSet ExpandAll={ExpandAll} initiallyExpanded={initiallyExpanded} allSizes={true}>
                    <BookingsList
                        id={getDomIdForFilter(BookingsFilter.Enquiries)}
                        bookingsFilter={BookingsFilter.Enquiries}
                        bookings={enquiryBookingsToRender}
                    />
                    <BookingsList
                        id={getDomIdForFilter(BookingsFilter.Quoted)}
                        bookingsFilter={BookingsFilter.Quoted}
                        bookings={quotedBookingsToRender}
                    />
                    <BookingsList
                        id={getDomIdForFilter(BookingsFilter.Booked)}
                        bookingsFilter={BookingsFilter.Booked}
                        bookings={bookedBookingsToRender}
                    />
                    <FlownBookingsLink />
                </ CollapsableSet>
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): BookingsListsStateProps {
    const requestStateProps = getRequestError(BookingsRequest);

    return {
        ...requestStateProps,
        isLoggedIn: userIsLoggedIn(state.auth),
        enquiryBookings: filterBookingsOnType(state.bookingsDetails, BookingsFilter.Enquiries).sort(bookingsComparison),
        quotedBookings: filterBookingsOnType(state.bookingsDetails, BookingsFilter.Quoted).sort(bookingsComparison),
        bookedBookings: filterBookingsOnType(state.bookingsDetails, BookingsFilter.Booked).sort(bookingsComparison),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): BookingsListsDispatchProps {
    return {
        fetchBookings: () => dispatch(new FetchRequest(new BookingsRequest()))
    };
}

export const BookingsLists = connectWithRequests(mapStateToProps, mapDispatchToProps)
(injectIntl(BookingsListsPresentational));
